/* eslint-disable @typescript-eslint/no-empty-interface */
export enum HttpMethod {
    Delete,
    Get,
    Head,
    Options,
    Patch,
    Post,
    Put,
}

function isStringArray(value: string[] | object | string): value is string[] {
    return Array.isArray(value);
}

function isObject(value: string[] | object | string): value is object {
    return (
        typeof value === 'object' &&
        !(value instanceof Number) &&
        !(value instanceof Boolean) &&
        !(value instanceof String) &&
        !(value instanceof Array)
    );
}

function isDate(value: Date | object): value is Date {
    return typeof value === 'object' && value instanceof Date;
}

function encodeKeyValue(key: string, value: string[] | object | string): string {
    if (isStringArray(value)) {
        return value.map(subValue => encodeKeyValue(`${key}[]`, subValue)).join('&');
    } else if (isObject(value)) {
        const encodedValue = isDate(value) ? value.toISOString() : JSON.stringify(value);
        return `${encodeURIComponent(key)}=${encodeURIComponent(encodedValue)}`;
    } else {
        return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    }
}

// Derived from https://stackoverflow.com/a/34209399

function objectToQueryStringEntries(obj: object): string {
    return Object.keys(obj)
        .filter((key: string) => (obj as any)[key] !== undefined)
        .map(key => encodeKeyValue(key, (obj as any)[key]))
        .join('&');
}

/**
 * Given "/api/clients/:clientId", and an object { clientId: 123 }, returns "/api/clients/123".
 *
 * @param input - The input string
 * @param obj - The tokens
 * @returns The input string replaces with the tokens
 */

function replaceKeysWithValues(input: string, obj: object): string {
    const tokens = Object.keys(obj);
    const pattern = new RegExp(`:(${tokens.join('|')})`, 'g');
    const result = input.replace(pattern, (_, matchingParam) => String((obj as any)[matchingParam]));
    return result;
}

export class PathBuilder<TParams> {
    protected readonly parts: string[] = [];

    public constructor(protected readonly prefix: string) {}

    public literal(part: string): this {
        this.parts.push(part);
        return this;
    }

    public param(part: string & keyof TParams): this {
        this.parts.push(`:${part}`);
        return this;
    }

    public build(options: { omitPrefix?: boolean } = {}): string {
        return (!options.omitPrefix ? `${this.prefix}/` : '') + this.parts.join('/');
    }
}

export type ObjectOrVoid = object | void;

export abstract class BaseRoute<TParams extends ObjectOrVoid, TQuery extends ObjectOrVoid, TRoute extends string> {
    protected readonly url: TRoute;

    protected constructor(prefix: string, url: string | ((path: PathBuilder<TParams>) => PathBuilder<TParams>)) {
        if (typeof url === 'string') {
            this.url = url as TRoute;
        } else {
            this.url = url(new PathBuilder(prefix)).build() as TRoute;
        }
    }

    public hostRoute(): TRoute {
        return this.url;
    }

    public consumerRouteBare(): TRoute {
        return this.url;
    }

    public consumerRouteWithParams(params: TParams): TRoute {
        return replaceKeysWithValues(this.url, params as object) as TRoute;
    }

    public addQueryString(url: TRoute, query: TQuery): TRoute {
        return `${url}?${objectToQueryStringEntries(query as object)}` as TRoute;
    }

    public constructBaseUrl(requestData: BaseRouteData<TParams, TQuery>): TRoute {
        let url: TRoute;
        if (requestData.params) {
            url = this.consumerRouteWithParams(requestData.params);
        } else {
            url = this.consumerRouteBare();
        }
        return url;
    }

    public constructUrlWithQueryString(requestData: BaseRouteData<TParams, TQuery>): TRoute {
        let url: TRoute = this.constructBaseUrl(requestData);
        if (requestData.query) {
            url = this.addQueryString(url, requestData.query);
        }
        return url;
    }
}

/**
 * Used for standard GET requests to server endpoints, in a non-API way, for traditional browser-based navigation.
 */
export class ServerRoute<TParams extends ObjectOrVoid = void, TQuery extends ObjectOrVoid = void> extends BaseRoute<
    TParams,
    TQuery,
    ServerRouteUrl
> {
    public readonly httpMethod: HttpMethod = HttpMethod.Get;

    public constructor(url: (path: PathBuilder<TParams>) => PathBuilder<TParams>) {
        super('', url);
    }
}

export class ApiRoute<

    _TResponse extends ObjectOrVoid = void,
    TParams extends ObjectOrVoid = void,

    _TBody extends ObjectOrVoid = void,
    TQuery extends ObjectOrVoid = void,
> extends BaseRoute<TParams, TQuery, ApiRouteUrl> {
    public constructor(
        public readonly httpMethod: HttpMethod,
        url: (path: PathBuilder<TParams>) => PathBuilder<TParams>,
    ) {
        super('/api', url);
    }
}

export interface BaseRouteData<TParams, TQuery> {
    params: TParams;
    query: TQuery;
}

export interface ApiRequestData<TParams, TBody, TQuery> {
    params: TParams;
    body: TBody;
    query: TQuery;
}

export function emptyRequest(): ApiRequestData<void, void, void> {
    return {
        body: undefined,
        params: undefined,
        query: undefined,
    };
}

export function justParams<TParams>(params: TParams): ApiRequestData<TParams, void, void> {
    return {
        body: undefined,
        params,
        query: undefined,
    };
}

export function justBody<TBody>(body: TBody): ApiRequestData<void, TBody, void> {
    return {
        body,
        params: undefined,
        query: undefined,
    };
}

export function justQuery<TQuery>(query: TQuery): ApiRequestData<void, void, TQuery> {
    return {
        body: undefined,
        params: undefined,
        query,
    };
}

export function paramsAndBody<TParams, TBody>(params: TParams, body: TBody): ApiRequestData<TParams, TBody, void> {
    return {
        body,
        params,
        query: undefined,
    };
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface AsApiRouteUrl {}
export type ApiRouteUrl = AsApiRouteUrl & string;
export function asApiRouteUrl(url: string): ApiRouteUrl {
    return url;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface AsServerRouteUrl {}
export type ServerRouteUrl = AsServerRouteUrl & string;
export function asServerRouteUrl(url: string): ServerRouteUrl {
    return url;
}
